import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { SectionLabel } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { CONTACT_EMAIL, MKT } from './links';
import { MarketingLayout } from './MarketingLayout';
import { TERMS_UPDATED } from './terms';
import './terms.css';
// The Terms of Service. Every rule here is one the app enforces: plans and allowances
// (models.Pricing), car passes and the sign-up guarantee (services/pass.go, services/guarantee.go
// — its in-app terms are GuaranteeTerms), the bid ledger and mailbox search (bid_ledger.go,
// win_mail.go), Stripe billing (stripe_billing.go). Change the version (terms.ts and
// models.TermsVersion) whenever the text changes in substance: checkout records which version a
// shop agreed to.
function Section({ id, k, title, children }) {
    return /*#__PURE__*/ _jsxs("section", {
        className: "mk-terms__section",
        id: id,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "mk-terms__k",
                "aria-hidden": "true",
                children: k
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "mk-terms__heading",
                        children: title
                    }),
                    children
                ]
            })
        ]
    });
}
export default function TermsPage() {
    const t = pricingText(pagePricing());
    return /*#__PURE__*/ _jsx(MarketingLayout, {
        testid: Testids.app.marketing.termsRoot,
        children: /*#__PURE__*/ _jsxs("main", {
            className: "mk-terms",
            children: [
                /*#__PURE__*/ _jsx(SectionLabel, {
                    k: "規約",
                    className: "mk-terms__label",
                    children: "Terms of Service"
                }),
                /*#__PURE__*/ _jsx("h1", {
                    className: "mk-terms__title",
                    children: "Terms of Service"
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "mk-terms__intro",
                    children: [
                        "The terms of service for Right Hand DMS, in plain language. Last updated ",
                        TERMS_UPDATED,
                        ". Questions: ",
                        CONTACT_EMAIL,
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsxs("nav", {
                    className: "mk-terms__toc",
                    "aria-label": "Sections",
                    children: [
                        /*#__PURE__*/ _jsx("a", {
                            href: "#agreement",
                            children: "Agreement"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#accounts",
                            children: "Accounts"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#service",
                            children: "What we are"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#logins",
                            children: "Logins & mail"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#billing",
                            children: "Plans & billing"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#passes",
                            children: "Car passes"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#guarantee",
                            children: "Sign-up guarantee"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#records",
                            children: "Records"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#use",
                            children: "Use"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#data",
                            children: "Your data"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#ending",
                            children: "Ending"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#liability",
                            children: "Liability"
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: "#changes",
                            children: "Changes"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "agreement",
                    k: "約",
                    title: "The agreement",
                    children: /*#__PURE__*/ _jsx("p", {
                        children: "These terms are between Right Hand DMS and you — the person using it and the shop (or individual importer account) you use it for. Creating an account, paying for a plan or car pass, or using the app means you agree to them. If you use it for a business, you confirm you can agree for that business."
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "accounts",
                    k: "店",
                    title: "Accounts and shops",
                    children: /*#__PURE__*/ _jsxs("ul", {
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                children: "Your account belongs to a shop. Everyone in a shop shares its cars, hunts, customers, documents, settings and connected logins; the shop’s owner is responsible for who it invites and what they do in it."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Keep your password to yourself and tell us at once if you think someone else has used it. You must be 18 or older and give us accurate details."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "One account per person; don’t share a login between people or between shops."
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs(Section, {
                    id: "service",
                    k: "道",
                    title: "What Right Hand DMS is — and isn’t",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "Right Hand DMS is software for finding, buying, importing and selling Japanese-market vehicles. We are not an auction house, an exporter, a customs broker, a shipper or a dealer. Your bids, purchases, deposits, shipping, customs entries and the legality of importing a vehicle (including the 25-year rule and EPA and DOT requirements) are between you and those parties, and are your responsibility."
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            children: "Auction sheet translations, bid ranges, match checks, landed-cost figures and other estimates are guidance, produced partly by AI, and can be wrong. Check the original sheet, listing and paperwork before you bid, buy or quote a customer."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "logins",
                    k: "鍵",
                    title: "Your auction logins and mailbox",
                    children: /*#__PURE__*/ _jsxs("ul", {
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                children: "When you connect an auction or exporter login, you let us use it to run your own searches, read lots and sheets and check results for you. You’re responsible for following those sites’ own terms."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "When you connect a mailbox, you let us send the emails you set up or approve and read the messages we need: replies to your requests, sheets, invoices and documents — and, for the sign-up guarantee, the search described below."
                            }),
                            /*#__PURE__*/ _jsxs("li", {
                                children: [
                                    "How we protect them is on our ",
                                    /*#__PURE__*/ _jsx("a", {
                                        href: MKT.security,
                                        children: "security page"
                                    }),
                                    ". You can disconnect either at any time."
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "billing",
                    k: "払",
                    title: "Plans and billing",
                    children: /*#__PURE__*/ _jsxs("ul", {
                        children: [
                            /*#__PURE__*/ _jsxs("li", {
                                children: [
                                    "Monthly plans are billed in advance each month by card through Stripe, our payment processor, and renew until cancelled. Prices are on the",
                                    ' ',
                                    /*#__PURE__*/ _jsx("a", {
                                        href: MKT.pricing,
                                        children: "pricing page"
                                    }),
                                    "; sales tax may be added where it applies."
                                ]
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "A price change applies to new subscriptions and plan changes. If we change what you already pay, we’ll tell you at least 30 days before it applies."
                            }),
                            /*#__PURE__*/ _jsxs("li", {
                                children: [
                                    "To cancel, email ",
                                    CONTACT_EMAIL,
                                    ". Your plan runs to the end of the month you’ve paid for; partial months aren’t refunded. Monthly plans don’t include the sign-up guarantee."
                                ]
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Each plan includes a monthly allowance of auction sheet reads and bid estimates. Unused allowance doesn’t carry over. Top-up packs last for the calendar month they’re bought in and aren’t refunded."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "If a payment fails, your account becomes past due and we may limit it until it’s paid."
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "passes",
                    k: "券",
                    title: "Import my own and car passes",
                    children: /*#__PURE__*/ _jsxs("ul", {
                        children: [
                            /*#__PURE__*/ _jsxs("li", {
                                children: [
                                    "Import my own is for individual importers. Searching is included, within its limits, for ",
                                    /*#__PURE__*/ _jsx("span", {
                                        ...priced('pass.days'),
                                        children: t['pass.days']
                                    }),
                                    " days from sign-up and from each car pass (and for a won car’s days while its pass covers it); with no pass active, hunts pause until one is added. Each car you win needs a car pass (",
                                    /*#__PURE__*/ _jsx("span", {
                                        ...priced('pass.price'),
                                        children: t['pass.price']
                                    }),
                                    "), which covers that car’s customs, US transport, documents and costs for",
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        ...priced('pass.days'),
                                        children: t['pass.days']
                                    }),
                                    " days from the win."
                                ]
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Signing up buys your first pass. An unused pass waits for your next win. Passes are one-time payments and aren’t refunded, except a sign-up pass under the guarantee."
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs(Section, {
                    id: "guarantee",
                    k: "還",
                    title: "The sign-up guarantee (Import my own only)",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "Only the pass you buy when signing up to Import my own carries this guarantee. Monthly plans, passes bought later and passes we grant don’t."
                        }),
                        /*#__PURE__*/ _jsxs("ul", {
                            children: [
                                /*#__PURE__*/ _jsxs("li", {
                                    children: [
                                        "If you haven’t won a car, the shop’s owner or an admin can ask for the sign-up back from Settings → Plan. We suggest giving it",
                                        ' ',
                                        /*#__PURE__*/ _jsx("span", {
                                            ...priced('guarantee.days'),
                                            children: t['guarantee.days']
                                        }),
                                        " days first; you can ask until ",
                                        /*#__PURE__*/ _jsx("span", {
                                            ...priced('guarantee.claim'),
                                            children: t['guarantee.claim']
                                        }),
                                        " days after that. The terms on your Plan page are the ones you signed up under."
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("li", {
                                    children: [
                                        "The refund is what you paid for the sign-up minus what you used at retail prices — currently ",
                                        /*#__PURE__*/ _jsx("span", {
                                            ...priced('guarantee.sheet'),
                                            children: t['guarantee.sheet']
                                        }),
                                        " per auction sheet and ",
                                        /*#__PURE__*/ _jsx("span", {
                                            ...priced('guarantee.comp'),
                                            children: t['guarantee.comp']
                                        }),
                                        " per bid estimate — shown before you ask and frozen when you do. Top-ups aren’t charged again and aren’t refunded."
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("li", {
                                    children: "It’s only for a shop that hasn’t won or bought a car at auction since signing up, through Right Hand DMS or anywhere else. Asking means stating that."
                                }),
                                /*#__PURE__*/ _jsx("li", {
                                    children: "When you ask, we look up the auction result of every bid on record, search your connected mailbox for win, invoice and purchase emails from exporters and auction sites since you signed up and keep copies of what we find with your request, and we may ask your exporter to confirm you haven’t bought a car. A request waits while a bid’s result is still coming in, and isn’t available if a result shows a win."
                                }),
                                /*#__PURE__*/ _jsx("li", {
                                    children: "We review each request and approve it or decline it with a reason; you can ask again while the guarantee is open. An approved refund goes back to your original payment method, and your sign-up pass is then void."
                                }),
                                /*#__PURE__*/ _jsx("li", {
                                    children: "A refund paid while you had won or bought a car is owed back in full, and we may charge it to your original payment method."
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "records",
                    k: "録",
                    title: "Records we keep",
                    children: /*#__PURE__*/ _jsx("p", {
                        children: "We keep a record of every bid you place or email through Right Hand DMS and the auction result we find for it, every car you mark won, your payments, refund requests and the evidence gathered for them, and each time you agree to these terms. We keep these even if you change or delete the car, for as long as we need them for billing, refunds and disputes — including after your account closes."
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "use",
                    k: "禁",
                    title: "Acceptable use",
                    children: /*#__PURE__*/ _jsxs("ul", {
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                children: "Don’t use Right Hand DMS for anything unlawful or to mislead anyone."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Don’t resell or share access outside your shop, try to get around plan limits, or use it in a way that breaks an auction site’s or exporter’s rules."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                children: "Don’t interfere with the service, probe it for weaknesses without our written permission, or copy it."
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "data",
                    k: "資",
                    title: "Your data",
                    children: /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Your shop’s data is yours. You let us store and process it to run the service for you, including with the AI providers that read sheets and documents for you. We don’t sell it. More on our ",
                            /*#__PURE__*/ _jsx("a", {
                                href: MKT.security,
                                children: "security page"
                            }),
                            "."
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "ending",
                    k: "終",
                    title: "Suspending and ending",
                    children: /*#__PURE__*/ _jsx("p", {
                        children: "You can stop using Right Hand DMS at any time. We may suspend or close an account that breaks these terms or doesn’t pay, after telling you why where we can. What you’ve already paid for runs to the end of its period unless the account was closed for breaking these terms."
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "liability",
                    k: "責",
                    title: "Warranty and liability",
                    children: /*#__PURE__*/ _jsx("p", {
                        children: "Right Hand DMS is provided as is. We work to keep it accurate and running, but we don’t promise it will be error-free or always available, or that a search will find every lot. We aren’t responsible for auction outcomes, missed or mistaken bids, a vehicle’s condition, shipping, customs or compliance. As far as the law allows, we aren’t liable for indirect or consequential losses, and our total liability is limited to what you paid us in the 12 months before the claim."
                    })
                }),
                /*#__PURE__*/ _jsx(Section, {
                    id: "changes",
                    k: "改",
                    title: "Changes to these terms",
                    children: /*#__PURE__*/ _jsx("p", {
                        children: "We may update these terms. The date at the top shows the latest version; we’ll email account owners about material changes before they apply, and using Right Hand DMS after that means you accept them. The version you agreed to when you paid is on record."
                    })
                })
            ]
        })
    });
}
